import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { filter } from 'rxjs';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { ConfirmDialogService } from '../../platform/admin/shared/confirm-dialog/confirm-dialog.service';

export interface NavChild {
  label: string;
  path: string;
}

export interface NavLink {
  label: string;
  path: string;
  exact?: boolean;
  highlight?: boolean;
  children?: NavChild[];
}

@Component({
  selector: 'app-header',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatMenuModule,
    AsyncPipe,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class HeaderComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly confirm = inject(ConfirmDialogService);
  @ViewChild('drawer') drawer?: MatSidenav;
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          this.closeIfMobile();
          this.menuTriggers?.forEach((t) => t.closeMenu());
        });
    }
  }

  @Input() hidden = false;
  @Input() scrolled = false;
  @Input() linksOverride: NavLink[] | null = null;
  @Input() actionLabel?: string;
  @Input() actionIcon?: string;
  @Output() actionClick = new EventEmitter<void>();

  readonly user$ = this.authService.user$;

  links: NavLink[] = [
    { label: 'Acasă', path: '', exact: true },
    { label: 'Ofertă Educațională', path: '/oferta' },
    { label: 'Înscrieri', path: '/inscrieri', highlight: true },
    { label: 'Kidoo', path: '/kidoo' },
    {
      label: 'Despre',
      path: '/despre-noi',
      children: [
        { label: 'Galerie', path: '/galerie' },
        { label: 'Noutăți', path: '/noutati' },
        { label: 'Cariere', path: '/cariere' },
      ],
    },
    { label: 'Contact', path: '/contact' },
  ];

  get navLinks(): NavLink[] {
    return this.linksOverride?.length ? this.linksOverride : this.links;
  }

  isChildActive(link: NavLink): boolean {
    if (!link.children?.length) return false;
    const url = this.router.url;
    return link.children.some((c) => url.startsWith(c.path));
  }

  get showPlatformShortcut(): boolean {
    return !this.navLinks.some((link) => link.path === '/platform');
  }

  get showHeaderLogout(): boolean {
    return !this.actionLabel;
  }

  closeIfMobile() {
    if (
      isPlatformBrowser(this.platformId) &&
      matchMedia('(max-width: 900px)').matches
    ) {
      this.drawer?.close();
    }
  }

  async logoutFromHeader() {
    const ok = await this.confirm.open({
      title: 'Deconectare',
      message: 'Ești sigur că vrei să te deconectezi?',
      confirmText: 'Deconectare',
      cancelText: 'Anulează',
      confirmColor: 'warn',
    });
    if (!ok) return;
    this.authService.logout().finally(() => this.closeIfMobile());
  }
}
